import React, { useState, useEffect } from 'react';
import { 
  Download,
  Filter,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  PieChart
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import budgetReportsService from '../../services/budgetReportsService';

const BudgetReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ categories: [], employees: [], departments: [] });
  
  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    categoryId: '',
    department: '',
    employeeId: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadFilterOptions();
    loadReportData();
  }, []);

  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const response = await budgetReportsService.getBudgetFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const response = await budgetReportsService.getBudgetReports(filters);
      if (response.success) {
        setReportData(response.data);
      } else {
        toast.error(response.message || 'Failed to load budget reports');
      }
    } catch (error) {
      console.error('Error loading budget reports:', error);
      if (!error._errorHandled) {
        toast.error('Failed to load budget reports');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      categoryId: '',
      department: '',
      employeeId: ''
    });
  };

  const exportReport = () => {
    toast.info('Export functionality coming soon');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Over Budget':
        return { bg: '#f8d7da', text: '#721c24', icon: AlertTriangle };
      case 'Near Budget':
        return { bg: '#fff3cd', text: '#856404', icon: AlertTriangle };
      case 'Within Budget':
        return { bg: '#d4edda', text: '#155724', icon: CheckCircle };
      default:
        return { bg: '#e2e3e5', text: '#383d41', icon: DollarSign };
    }
  };

  if (loading && !reportData) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading budget reports...</p>
        </div>
      </div>
    );
  }

  const summary = reportData?.summary || {};
  const categoryBudgetComparison = reportData?.categoryBudgetComparison || [];
  const employeeBudgetComparison = reportData?.employeeBudgetComparison || [];
  const departmentBudgetComparison = reportData?.departmentBudgetComparison || [];
  const actualExpensesByMonth = reportData?.actualExpensesByMonth || [];
  const topOverBudget = reportData?.topOverBudget || [];

  const summaryStatus = getStatusColor(summary.status || 'No Budget');
  const SummaryIcon = summaryStatus.icon;

  // Calculate max for chart scaling
  const maxCategoryAmount = Math.max(
    ...categoryBudgetComparison.map(c => Math.max(c.budgetAmount || 0, c.actualAmount || 0)),
    1
  );
  const maxMonthlyAmount = Math.max(...actualExpensesByMonth.map(m => m.actualAmount || 0), 1);

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Expenses', path: '/expenses' },
          { label: 'Budget Reports', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Budget Reports</h1>
          <p>Budget vs actual expense analysis</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            Filters
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportReport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Category
              </label>
              <select
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Categories</option>
                {filterOptions.categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.categoryName}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Department
              </label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Departments</option>
                {filterOptions.departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Employee
              </label>
              <select
                value={filters.employeeId}
                onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Employees</option>
                {filterOptions.employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.totalBudget)}
          </div>
          <div className="metric-title">Total Budget</div>
          <div className="metric-subtitle">All budget plans</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.totalActual)}
          </div>
          <div className="metric-title">Total Actual</div>
          <div className="metric-subtitle">All actual expenses</div>
        </div>

        <div className={`metric-card ${summary.variance >= 0 ? 'churn' : 'burn'}`}>
          <div className="metric-header">
            <div className="metric-icon">
              {summary.variance >= 0 ? (
                <TrendingUp size={16} />
              ) : (
                <TrendingDown size={16} />
              )}
            </div>
            <div className={`metric-trend ${summary.variance >= 0 ? 'negative' : 'positive'}`}>
              <span>{summary.variance >= 0 ? 'Over' : 'Under'}</span>
            </div>
          </div>
          <div className="metric-value" style={{ color: summary.variance >= 0 ? '#f5576c' : '#43e97b' }}>
            {summary.variance >= 0 ? '+' : ''}{formatCurrency(summary.variance)}
          </div>
          <div className="metric-title">Variance</div>
          <div className="metric-subtitle">Budget vs actual</div>
        </div>

        <div className="metric-card burn" style={{ 
          background: summaryStatus.bg, 
          border: `1px solid ${summaryStatus.text}` 
        }}>
          <div className="metric-header">
            <div className="metric-icon" style={{ background: summaryStatus.text }}>
              <SummaryIcon size={16} color="#fff" />
            </div>
            <div className="metric-trend neutral">
              <span>Status</span>
            </div>
          </div>
          <div className="metric-value" style={{ color: summaryStatus.text }}>
            {summary.status || 'No Budget'}
          </div>
          <div className="metric-title" style={{ color: summaryStatus.text }}>Status</div>
          <div className="metric-subtitle" style={{ color: summaryStatus.text, opacity: 0.8 }}>
            {summary.variancePercentage !== 0 && (
              <span>{summary.variancePercentage >= 0 ? '+' : ''}{summary.variancePercentage.toFixed(1)}%</span>
            )}
            {summary.variancePercentage === 0 && 'Budget status'}
          </div>
        </div>
      </div>

      {/* Top Over Budget Items */}
      {topOverBudget.length > 0 && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={20} color="#f5576c" />
            Top Over Budget Items
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Item</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Budget</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Actual</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Variance</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>%</th>
                </tr>
              </thead>
              <tbody>
                {topOverBudget.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                      {item.categoryName || item.employeeName || item.department || 'Unknown'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                      {formatCurrency(item.budgetAmount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#f5576c', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(item.actualAmount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#f5576c', textAlign: 'right', fontWeight: '600' }}>
                      +{formatCurrency(item.variance)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#f5576c', textAlign: 'right', fontWeight: '600' }}>
                      +{item.variancePercentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Budget Comparison */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Category Budget vs Actual
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Budget</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Actual</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Variance</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>%</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {categoryBudgetComparison.length > 0 ? (
                categoryBudgetComparison.map((category, index) => {
                  const statusColors = getStatusColor(category.status);
                  const budgetWidth = (category.budgetAmount / maxCategoryAmount) * 100;
                  const actualWidth = (category.actualAmount / maxCategoryAmount) * 100;
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                        {category.categoryName}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                        {formatCurrency(category.budgetAmount)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                        {formatCurrency(category.actualAmount)}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: category.variance >= 0 ? '#f5576c' : '#43e97b', 
                        textAlign: 'right',
                        fontWeight: '600'
                      }}>
                        {category.variance >= 0 ? '+' : ''}{formatCurrency(category.variance)}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: category.variance >= 0 ? '#f5576c' : '#43e97b', 
                        textAlign: 'right',
                        fontWeight: '600'
                      }}>
                        {category.variancePercentage >= 0 ? '+' : ''}{category.variancePercentage.toFixed(1)}%
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: statusColors.bg,
                          color: statusColors.text
                        }}>
                          {category.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No category budget data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Department Budget Comparison */}
      {departmentBudgetComparison.length > 0 && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Department Budget vs Actual
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Budget</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Actual</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Variance</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>%</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {departmentBudgetComparison.map((dept, index) => {
                  const statusColors = getStatusColor(dept.status);
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                        {dept.department}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                        {formatCurrency(dept.budgetAmount)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                        {formatCurrency(dept.actualAmount)}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: dept.variance >= 0 ? '#f5576c' : '#43e97b', 
                        textAlign: 'right',
                        fontWeight: '600'
                      }}>
                        {dept.variance >= 0 ? '+' : ''}{formatCurrency(dept.variance)}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: dept.variance >= 0 ? '#f5576c' : '#43e97b', 
                        textAlign: 'right',
                        fontWeight: '600'
                      }}>
                        {dept.variancePercentage >= 0 ? '+' : ''}{dept.variancePercentage.toFixed(1)}%
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: statusColors.bg,
                          color: statusColors.text
                        }}>
                          {dept.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Budget vs Actual Chart */}
      {actualExpensesByMonth.length > 0 && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
            Monthly Actual Expenses
          </h3>
          <div style={{ height: '300px', display: 'flex', alignItems: 'flex-end', gap: '12px', paddingBottom: '40px' }}>
            {actualExpensesByMonth.map((month, index) => {
              const height = (month.actualAmount / maxMonthlyAmount) * 100;
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return (
                <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '100%',
                    height: `${height}%`,
                    background: 'linear-gradient(180deg, #f5576c 0%, #f093fb 100%)',
                    borderRadius: '4px 4px 0 0',
                    minHeight: '4px',
                    cursor: 'pointer'
                  }}
                  title={formatCurrency(month.actualAmount)}
                  ></div>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', textAlign: 'center' }}>
                    {monthNames[month._id.month - 1]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetReports;

